// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import {
    ApiHandler,
    createHandlerChainRunner,
    HasOnError,
    HasOnSuccess,
    markSkipOnCompleteIfOnErrorIsSet,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import {
    exportInvoiceHistory,
    exportInvoiceHistoryUrl,
} from "@stoneage/App/Services/InovoiceHistory/ExportInvoiceHistory";
import { GetInvoicesApiParameter } from "@insite/client-framework/Services/InvoiceService";
import { ServiceResult } from "@insite/client-framework/Services/ApiService";

type Parameter = GetInvoicesApiParameter & HasOnSuccess & HasOnError<string>;
type HandlerType = ApiHandler<Parameter, ServiceResult<string>>;

const getInvoicesParameterString = (invoicesParameter: GetInvoicesApiParameter): string => {
    const params = new URLSearchParams();

    Object.entries(invoicesParameter).forEach(([key, value]) => {
        if (value === undefined || value === null) return;

        if (Array.isArray(value)) {
            value.forEach(item => params.append(key, item));
        } else {
            params.append(key, String(value));
        }
    });

    return params.toString();
};

export const PopulateApiParameter: HandlerType = async props => {
    props.apiParameter = props.getState().pages.invoiceHistory.getInvoicesParameter;
};

export const RequestDataFromApi: HandlerType = async props => {
    props.apiResult = await exportInvoiceHistory(props.apiParameter);
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    if (props.apiResult.successful) {
        markSkipOnCompleteIfOnSuccessIsSet(props);

        const invoicesParameter = props.getState().pages.invoiceHistory.getInvoicesParameter;
        const parameter = getInvoicesParameterString(invoicesParameter);

        if (typeof window !== "undefined") {
            window.open(`${exportInvoiceHistoryUrl}?${parameter}`, "_self");
        }
        props.parameter.onSuccess?.();
        return false;
    }
};

export const ExecuteOnErrorCallback: HandlerType = props => {
    if (props.apiResult && !props.apiResult.successful) {
        markSkipOnCompleteIfOnErrorIsSet(props);
        props.parameter.onError?.(props.apiResult.errorMessage);
    }
};

export const chain = [PopulateApiParameter, RequestDataFromApi, ExecuteOnSuccessCallback, ExecuteOnErrorCallback];

const exportInvoiceHistoryCsv = createHandlerChainRunner(chain, "ExportInvoiceHistoryCsv");
export default exportInvoiceHistoryCsv;
